import React from 'react';
import { Drawer } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { colorPalette, Lang } from 'shared/constants';
import { IconButton } from '../../buttons';
import { SectionTitle } from '../../titles';
import { ShareBoxComponent } from 'src/components/share';
import styles from './content-binder-drawer.module.scss';

/**
 * @typedef {Object} ShareBoxProps
 * @property {string} path - The share path
 * @property {string} id - Entity ID
 * @property {string} title - Entity title
 * @property {Object} tags - Entity tags
 * @property {string} entityType - Type of entity being shared
 */

/**
 * @typedef {Object} ContentBinderDrawerProps
 * @property {string} title - Title displayed in drawer header
 * @property {React.ReactNode} [icon] - Rendered left of the title, spanning title + subtitle
 * @property {React.ReactNode} [subtitle] - Rendered below the title, aligned with it
 * @property {ShareBoxProps} shareBoxProps - Props for ShareBoxComponent
 */

/**
 * ContentBinderDrawer - A standardized drawer component for content binders
 * (admission, knowledge, lab, etc.) with consistent header styling.
 *
 * @param {ContentBinderDrawerProps & import('antd').DrawerProps} props
 */
function ContentBinderDrawer({
  title,
  badge,
  icon,
  subtitle,
  shareBoxProps,
  langTooltip = Lang.ACTION_BUTTON_TOOLTIPS,
  width = '40vw',
  animated = true,
  onClose,
  children,
  ...drawerProps
}) {
  const hasActions = !!shareBoxProps;

  return (
    <Drawer
      onClose={onClose}
      closable={false}
      mask={false}
      focusable={{ focusTriggerAfterClose: true }}
      className="new-design-system"
      styles={{
        body: { padding: '1rem 1.5rem' },
        header: { padding: 'clamp(1rem, 1rem, 10rem) 1.5rem' },
        wrapper: {
          width,
          top: 'var(--header-height)',
          height: 'calc(100% - var(--header-height))',
        },
      }}
      {...drawerProps}
      {...(!animated && { motion: false })}
      title={
        <div className={styles.drawerHeader}>
          <div className={styles.header}>
            {icon}
            <div className={styles.titleColumn}>
              <div className={styles.titleRow}>
                <div className={styles.titleAndBadge}>
                  {title && <SectionTitle title={title} wrapperClassName={styles.titleWrapper} />}
                  {badge}
                </div>
                {hasActions && (
                  <div className={styles.actions}>
                    <ShareBoxComponent
                      {...shareBoxProps}
                      iconClassName={styles.shareBoxIcon}
                      className={styles.shareBox}
                    />
                  </div>
                )}
              </div>
              {subtitle}
            </div>
          </div>
          <IconButton
            tooltipProps={{ placement: 'left' }}
            onClick={onClose}
            label={langTooltip.CLOSE}>
            <FontAwesomeIcon icon={faXmark} color={colorPalette.primaryMain8} size="xl" />
          </IconButton>
        </div>
      }>
      {children}
    </Drawer>
  );
}

export { ContentBinderDrawer };
